<script setup lang="ts">
import type {
	ProductContentsResponseSchema,
	ProductSchema,
} from '@laam/cms-shared';
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	Checkbox,
	type CarouselApi,
} from '@laam/ui/base';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';
import { watchOnce } from '@vueuse/core';
import { computed, provide, ref, toRaw, toRefs } from 'vue';
import { isMobileView } from '../../../utils/helpers.ts';
import ImageViewer from '../ImageViewer.vue';
import ImageViewerMobile from '../ImageViewerMobile.vue';
import CarouselThumbnail from './CarouselThumbnail.vue';
import { MediaType, type Media } from './types.ts';
import { videosMapper } from './utils.ts';

interface Props {
	productData?: ProductSchema;
	parent?: string;
	alt: string;
	productContents?: ProductContentsResponseSchema;
}
const props = defineProps<Props>();

const { productContents } = toRefs(props);

//CMS specific rendering
const emit = defineEmits<{
	(e: 'video-selected', video_id: number | undefined): void;
}>();
const selectedVideo = ref<Media | null>(null);

const contentVideosForCms = computed(() => {
	return productContents?.value?.contents?.map((content) =>
		toRaw(content.videos),
	);
});

const isVideoSelected = (mediaItem: Media) => {
	return (
		selectedVideo.value?.src === mediaItem.src &&
		mediaItem.type === MediaType.VIDEO
	);
};

const selectVideoForCms = (checked: boolean, mediaItem: Media) => {
	selectedVideo.value = checked ? mediaItem : null;
	emit('video-selected', selectedVideo.value?.content_id);
};

//component rendering
const isImageViewerOpen = ref<boolean>(false);
const emblaMainApi = ref<CarouselApi>();
const selectedIndex = ref(0);

const onOpenImageViewer = () => {
	isImageViewerOpen.value = true;
};

function onSelect() {
	if (!emblaMainApi.value) return;
	selectedIndex.value = emblaMainApi.value.selectedScrollSnap();
}

const media = computed(() => {
	if (productContents.value) {
		// @ts-ignore
		return videosMapper(contentVideosForCms.value, undefined);
	} else {
		return videosMapper(props.productData?.content_videos);
	}
});

watchOnce(emblaMainApi, (emblaMainApi) => {
	if (!emblaMainApi) return;

	onSelect();
	emblaMainApi.on('select', onSelect);
	emblaMainApi.on('reInit', onSelect);
});

const poster = computed(() => props.productData?.media[0]?.src ?? '');

provide('carousel-type', 'styling-carousel');
provide('productData', props.productData);
</script>

<template>
	<div class="gap-3xl lg:gap-md">
		<Carousel
			class="lg:block relative select-none"
			:opts="{
				align: 'start',
				loop: true,
			}"
			@init-api="(val) => (emblaMainApi = val)"
		>
			<div class="flex flex-col gap-md">
				<span v-if="productContents" class="text-xl font-semibold text-gray-700">Select a video</span>

				<CarouselContent class="flex gap-1 ml-0 justify-start">
					<CarouselItem
						v-for="(slide, index) in media"
						:key="index"
						class="flex flex-col gap-md !p-0 !pr-md basis-[30.5%] cursor-pointer"
						@click="onOpenImageViewer"
					>
						<Checkbox
							v-if="slide.type === MediaType.VIDEO && productContents"
							class="!border-[2px]"
							:checked="isVideoSelected(slide)"
							@update:checked="
								(checked: boolean) => selectVideoForCms(checked, slide)
							"
						>
							Select Video
						</Checkbox>
						<CarouselThumbnail
							:type="slide.type"
							:src="
								slide.type === MediaType.IMAGE
									? slide.src
									: slide?.poster ?? poster
							"
							:alt="alt"
							:is-active="index === selectedIndex"
							:is-style="true"
						/>
					</CarouselItem>
				</CarouselContent>
				<ImageViewerMobile
					v-if="isMobileView()"
					v-model="isImageViewerOpen"
					:media="media"
					:alt="props.alt"
					:index="selectedIndex"
					:fallback-poster="poster"
				/>
				<ImageViewer
					v-else
					v-model="isImageViewerOpen"
					:media="media"
					:alt="props.alt"
					:index="selectedIndex"
					:fallback-poster="poster"
				/>
			</div>
			<CarouselPrevious v-if="!isMobileView()" class="navigation_button">
				<PhCaretLeft weight="bold" />
			</CarouselPrevious>
			<CarouselNext v-if="!isMobileView()" class="navigation_button">
				<PhCaretRight weight="bold" />
			</CarouselNext>
		</Carousel>
	</div>
</template>

<style scoped>
.navigation_button {
	@apply !size-8xl !flex top-[50%] -translate-y-[50%] [&_svg]:size-4xl border-gray-500 border-[1px];
}
</style>
